import { ViewportScroller, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule, Scroll } from '@angular/router';
import { Subject, filter, map, takeUntil, withLatestFrom } from 'rxjs';
import { NotificationModalComponent } from './shared/components/notification-modal/notification-modal.component';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ImagePreviewComponent } from './shared/components/image-preview/image-preview.component';
import { SegmentService } from 'segment';

@Component({
  imports: [RouterModule, NotificationModalComponent, ImagePreviewComponent],
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
    `,
  ],
  selector: 'ss-www-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnDestroy {
  private segmentService = inject(SegmentService);
  private viewPortScroller = inject(ViewportScroller);
  private readonly elRef = inject(ElementRef);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  private destroy$ = new Subject<void>();

  constructor() {
    dayjs.extend(timezone);
    dayjs.extend(utc);
    this.viewPortScroller.setOffset([0, 112]);

    // withInMemoryScrolling() adds scroll events to the router
    // however, the default behavior is not desired
    // because it does not allow bypassing scroll events
    // when the router change is only a query param change
    // This is due to the info carousel component
    const scrollEvents$ = this.router.events.pipe(
      filter((event) => event instanceof Scroll),
      map((event) => event as Scroll)
    );

    const navEnd$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event as NavigationEnd)
    );

    if (isPlatformBrowser(this.platformId)) {
      scrollEvents$
        .pipe(withLatestFrom(navEnd$), takeUntil(this.destroy$))
        .subscribe(([scrollEvent]) => {
          if (history?.state?.['ignoreScroll']) return;
          if (scrollEvent.position) {
            // backward navigation
            this.viewPortScroller.scrollToPosition(scrollEvent.position);
          } else if (scrollEvent.anchor) {
            // anchor navigation
            this.viewPortScroller.scrollToAnchor(scrollEvent.anchor);
            // allow for router state to ignore scroll
          } else {
            this.viewPortScroller.scrollToPosition([0, 0]);
          }
        });

      navEnd$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.segmentService.trackPage();
      });
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
